import React from 'react'
import { Table, TableHead, TableRow, TableCell, TableBody, Stack, Typography, Box, Chip, Divider, LinearProgress} from '@mui/material'
import ChevronUpIcon from '@mui/icons-material/ExpandLess'
import ChevronDownIcon from '@mui/icons-material/ExpandMore'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { visuallyHidden } from '@mui/utils'

// field 대신 role 구분
function findByRole(columns, role) {
  return columns.find((col) => col.role === role)
}

export default function BoardTableView({
  rows = [],
  columns = [],
  sort,
  onSort,
  getRowId,
  onRowClick,
  emptyMessage = '데이터가 없습니다.',
  forceTableView = false,
  showStudentDetails,
  caption,
}) {
  const { isMobile } = useIsMobile()
  const lang = useLang()
  const showMobileCardView = isMobile && !forceTableView

  /* ===== role 기반 컬럼 참조 ===== */
  const noCol     = findByRole(columns, 'no')
  const titleCol  = findByRole(columns, 'title')
  const dateCol   = findByRole(columns, 'date')
  const attachCol = findByRole(columns, 'attachment')
  const qcatCol    = findByRole(columns, 'qCategory')
  const vlabCol   = findByRole(columns, 'vlab')
  const lectureCol = findByRole(columns, 'lecture')
  const statusCol  = findByRole(columns, 'status')
  const institutionCol  = findByRole(columns, 'institution')
  const departmentCol   = findByRole(columns, 'department')
  const nameCol         = findByRole(columns, 'name')
  const sessionCol      = findByRole(columns, 'session')
  const totalSessionCol = findByRole(columns, 'totalSession')
  const surveyCol       = findByRole(columns, 'survey')

  // 모바일
  if (showMobileCardView) {
    if (!rows.length) {
      return (
        <>
          <Divider sx={{ borderColor: 'primary.main', borderTopWidth: 1 }} />
          <Stack alignItems="center" spacing={1.5} sx={{ py: 15 }}>
            <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
            <Typography color="grey.500">{emptyMessage}</Typography>
          </Stack>
          <Divider sx={{ borderColor: 'divider' }} />
        </>
      )
    }

    return (
      <>
        <Divider sx={{ borderColor: 'primary.main', borderTopWidth: 1 }} />

        {rows.map((row, index) => {
          const id = getRowId?.(row)

          const no         = noCol ? row[noCol.field] : null
          const title      = titleCol ? row[titleCol.field] : null
          const createdAt  = dateCol ? row[dateCol.field] : null
          const attachments= attachCol ? row[attachCol.field] : []
          // const noticeYn = String(row.noticeYn ?? '').trim()
          const bbsTypeName = String(row.bbsTypeName ?? '').trim()
          const qCategory = qcatCol ? row[qcatCol.field] : null
          const lecture = lectureCol ? row[lectureCol.field] : null
          const status  = statusCol ? row[statusCol.field] : null
          const institution  = institutionCol  ? row[institutionCol.field]  : null
          const department   = departmentCol   ? row[departmentCol.field]   : null
          const name         = nameCol         ? row[nameCol.field]         : null
          const session = sessionCol
            ? Number(row[sessionCol.field])
            : row.session != null
              ? Number(row.session)
              : null
          const totalSession = totalSessionCol
            ? Number(row[totalSessionCol.field])
            : row.totalSession != null
              ? Number(row.totalSession)
              : null
          const survey       = surveyCol       ? row[surveyCol.field]       : null

          let category = ''
          let showChip = false

          if (bbsTypeName === '공지') {
            category = '공지'
            showChip = true
          } else if (bbsTypeName === '전체') {
            category = '전체'
            showChip = true
          }
          const vlab = vlabCol
            ? Boolean(row[vlabCol.field])
            : String(row.vlab) === 'true'

          return (
            <Box
              key={id ?? `row-${index}`}
              sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 3 }}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {/* 라벨 */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                {bbsTypeName === '전체' ? (
                  <Typography color="primary.main" fontWeight={700}>VLab</Typography>
                ) : category === '공지' ? (
                  <Typography color="primary.main" fontWeight={700}>중요</Typography>
                ) : (
                  <Typography fontWeight={500}>NO. {no}</Typography>
                )}

                {showChip && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: '1px', height: '12px', bgcolor: 'divider' }} />
                    <Chip
                      label={category}
                      variant="outlined"
                      color={category === '전체' ? 'primary' : 'grey'}
                    />
                  </Stack>
                )}

                {status && (
                  <Chip
                    label={`답변${status}`}
                    size="small"
                    variant="outlined"
                    color={status === '대기' ? 'secondary' : 'secondary2'}
                    sx={{ ml: 'auto !important' }}
                  />
                )}
              </Stack>

              {/* 분류 / 강좌 */}
              {(qCategory || lecture) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {qCategory && lecture
                    ? `${qCategory} > ${lecture}`
                    : qCategory || lecture}
                </Typography>
              )}

              {/* 제목 */}
              {titleCol && (
                <Box>
                  {titleCol.render
                    ? titleCol.render(row[titleCol.field], row)
                    : row[titleCol.field]}
                </Box>
              )}
              {/* {title && (
                <Box sx={{ display: 'block' }}>
                  {title}
                </Box>
              )} */}

              {/* 하단 */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1 }}
              >
                {createdAt && (
                  <Typography color="text.secondary" fontWeight={500}>
                    {createdAt}
                  </Typography>
                )}
                {Array.isArray(attachments) && attachments.length > 0 && (
                  <AttachFileIcon sx={{ color: 'grey.dark' }} />
                )}
              </Stack>

              {showStudentDetails && (
                <Stack spacing={1}>
                  {/* 기관 / 학과 */}
                  {(institution || department) && (
                    <Typography variant="subtitle3" fontWeight={400}>
                      {institution}
                      {department && ` / ${department}`}
                    </Typography>
                  )}

                  {/* 이름 */}
                  {name && (
                    <Typography variant="subtitle1" fontWeight={600}>
                      {name}
                    </Typography>
                  )}

                  {/* 진행 / 설문 */}
                  <Stack direction="row">
                    {/* 진행상태 */}
                    {(session != null && totalSession != null) && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ flex: 1 }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          진행상태
                        </Typography>
                        <Typography fontWeight={500}>
                          {session} / {totalSession}
                        </Typography>
                      </Stack>
                    )}

                    {/* 설문진행 */}
                    {survey && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ flex: 1 }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          설문진행
                        </Typography>
                        <Typography fontWeight={500}>
                          {survey}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {/* 진도율 */}
                  {(session != null && totalSession) && (() => {
                    const percent = Math.min(
                      Math.round((session / totalSession) * 100),
                      100
                    )

                    return (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ width: '100%' }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          진도율
                        </Typography>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ flex: 1 }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                              width: '100%',
                              height: '8px',
                              borderRadius: 1,
                              bgcolor: 'grey.100',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                          <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
                            {percent}%
                          </Typography>
                        </Stack>
                      </Stack>
                    )
                  })()}
                </Stack>
              )}

            </Box>
          )
        })}
      </>
    )
  }

  // PC + mobile table
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table 
        sx={{ 
          width: '100%', 
          tableLayout: 'fixed',
          '& caption': visuallyHidden,
        }}
      >
        {caption && <caption>{caption}</caption>}
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.field}
                align="center"
                variant={lang === 'en' && !isMobile ? 'body2' : 'body1'}
                sx={{
                  width: col.width ?? undefined,
                  minWidth: col.minWidth ?? col.width,
                  borderTop: '2px solid',
                  borderTopColor: 'primary.main',
                  bgcolor: 'primary.50',
                  color: 'text.secondary',
                  whiteSpace: lang === 'ko' ? 'initial' : 'nowrap',
                }}
              >
                {col.sortable ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    onClick={() => onSort?.(col.field)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Typography fontWeight={500} variant={lang === 'en' && !isMobile ? 'body2' : 'body1'}>
                      {col.headerName ?? col.field}
                    </Typography>
                    {sort?.field === col.field ? (
                      sort.direction === 'asc'
                        ? <ChevronUpIcon fontSize="small" />
                        : <ChevronDownIcon fontSize="small" />
                    ) : (
                      <ChevronDownIcon fontSize="small" />
                    )}
                  </Stack>
                ) : (
                  <Typography fontWeight={500} variant={lang === 'en' && !isMobile ? 'body2' : 'body1'}>
                    {col.headerName ?? col.field}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {!rows.length ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 15 }}>
                <Stack alignItems="center" spacing={1.5}>
                  <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
                  <Typography color="grey.500">{emptyMessage}</Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => {
              const rowId = getRowId?.(row)

              return (
                <TableRow
                  key={rowId ?? `row-${index}`}
                  hover
                >
                  {columns.map((col) => {
                    const content = col.render
                      ? col.render(row[col.field], row)
                      : row[col.field]

                    return (
                      <TableCell
                        key={col.field}
                        align={col.align || 'center'}
                        sx={{ typography: 'body1' }}
                      >
                        {Array.isArray(content)
                          ? content.map((c, i) => (
                              <React.Fragment key={`${col.field}-${i}`}>
                                {c}
                              </React.Fragment>
                            ))
                          : content}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })
          )}
        </TableBody>

      </Table>
    </Box>
  )
}
